import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Facebook, RefreshCw, Calendar, ArrowRight } from "lucide-react";
import { AdsFreshness, AdsReconnectBanner } from "@/components/agency/AdsSyncBadge";
import { useTranslation } from "react-i18next";
import { fmtMoney, fmtNumber } from "@/lib/format";

/**
 * META FASE 4 — ROI Ads tenant. Semantica OPERATIVA per periodo (i ratio
 * possono superare il 100%). Spesa automatica da Meta (meta_ads_daily_metrics)
 * con fallback manuale solo se Meta non ha dati. Nessun dettaglio
 * campaign/adset/ad/creative mostrato al tenant.
 */
const fmtEur = (n, cur = "EUR") => fmtMoney(n, { currency: cur || "EUR" });
const fmtPct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);
const fmtNum = (n) => fmtNumber(n || 0);

export default function AdsKPI() {
  const { t } = useTranslation();
  const today = new Date();
  const daysAgo = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const [since, setSince] = useState(daysAgo(30));
  const [until, setUntil] = useState(daysAgo(0));
  const [manualSpend, setManualSpend] = useState(() => {
    const s = localStorage.getItem("ads_spend_input"); return s ? Number(s) : 0;
  });
  const [data, setData] = useState(null);
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = `start_date=${since}&end_date=${until}`;
      // Il fallback manuale viene inviato: il backend lo usa SOLO se Meta non ha dati.
      if (manualSpend > 0) q += `&manual_spend=${manualSpend}`;
      const r = await api.get(`/meta/ads/roi?${q}`);
      setData(r.data);
      try {
        const h = await api.get("/meta/ads/sync-health");
        setSync(h.data);
      } catch { setSync(null); }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("AdsKPI load failed", e?.response?.data || e?.message);
      setData(null);
    }
    setLoading(false);
  }, [since, until, manualSpend]);
  useEffect(() => { load(); }, [load]);

  const cur = data?.currency || "EUR";
  const isMeta = data?.spend_source === "meta";

  return (
    <div data-testid="ads-kpi-page" className="space-y-6">
      {/* Header + date range */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black">{t("ads.title")}</h1>
          <p className="text-sm text-[color:var(--sp-text-muted)]">{t("ads.subtitle")}</p>
          <AdsFreshness sync={sync} className="mt-1" />
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => { setSince(daysAgo(d)); setUntil(daysAgo(0)); }}
              data-testid={`preset-${d}`}
              className="text-xs font-bold px-2.5 py-1.5 rounded-lg border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]">
              {t("ads.days", { n: d })}
            </button>
          ))}
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="date-since" className="sp-input !py-1 !text-xs" />
            <span>→</span>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="date-until" className="sp-input !py-1 !text-xs" />
          </div>
          <button onClick={load} data-testid="refresh-btn" className="p-2 rounded-lg border border-[color:var(--sp-border)]">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !data && <div className="text-sm text-[color:var(--sp-text-muted)]">{t("common.loading")}</div>}

      <AdsReconnectBanner sync={sync} message={t("ads.reconnect")} />

      {data && (
        <>
          {/* RIGA 1 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi tid="kpi-spend" label={t("ads.spend")} value={fmtEur(data.spend, cur)}
              badge={isMeta ? t("ads.syncedFromMeta") : t("ads.metaUnavailable")} badgeOk={isMeta} />
            <Kpi tid="kpi-leads" label={t("ads.leads")} value={fmtNum(data.leads)}
              sub={data.leads_source === "meta"
                ? t("ads.leadsBreakdown", { form: fmtNum(data.meta_form_leads || 0), pixel: fmtNum(data.meta_pixel_leads || 0) })
                : t("ads.leadsCrmFallback")} />
            <Kpi tid="kpi-cpl" label={t("ads.cpl")} value={data.cpl != null ? fmtEur(data.cpl, cur) : "—"} sub={t("ads.cplSub")} />
            <Kpi tid="kpi-new-customers" label={t("ads.newCustomers")} value={fmtNum(data.new_customers)} />
          </div>

          {/* Spesa manuale (solo fallback) */}
          {!isMeta && (
            <div data-testid="manual-spend-box" className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 flex items-center gap-3">
              <span className="text-sm font-bold text-amber-700">{t("ads.manualSpend")}</span>
              <input type="number" value={manualSpend} data-testid="manual-spend-input"
                onChange={(e) => { const v = Number(e.target.value); setManualSpend(v); localStorage.setItem("ads_spend_input", String(v)); }}
                className="sp-input !py-1 w-32" />
              <span className="text-xs text-amber-700">{t("ads.manualSpendHint")}</span>
            </div>
          )}

          {/* RIGA 2 */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi tid="kpi-appts" label={t("ads.appointments")} value={fmtNum(data.appointments_set)} />
            <Kpi tid="kpi-setting" label={t("ads.settingRate")} value={fmtPct(data.setting_rate)} sub={t("ads.settingSub")} />
            <Kpi tid="kpi-cpa" label={t("ads.cpa")} value={data.cpa != null ? fmtEur(data.cpa, cur) : "—"} sub={t("ads.cpaSub")} />
            <Kpi tid="kpi-showed" label={t("ads.showed")} value={fmtNum(data.showed_up)} />
            <Kpi tid="kpi-showup" label={t("ads.showUp")} value={fmtPct(data.show_up_rate)} sub={t("ads.showUpSub")} />
          </div>

          {/* RIGA 3 */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi tid="kpi-cac" label={t("ads.cac")} value={data.cac != null ? fmtEur(data.cac, cur) : "—"} sub={t("ads.cacSub")} />
            <Kpi tid="kpi-fe" label={t("ads.frontEnd")} value={fmtEur(data.front_end_revenue, cur)} />
            <Kpi tid="kpi-be" label={t("ads.backEnd")} value={fmtEur(data.back_end_revenue, cur)} />
            <Kpi tid="kpi-total-rev" label={t("ads.totalRevenue")} value={fmtEur(data.total_revenue, cur)} highlight />
            <Kpi tid="kpi-total-roas" label={t("ads.totalRoas")} value={data.total_roas != null ? `${data.total_roas}×` : "—"}
              sub={data.front_end_roas != null ? t("ads.feRoas", { v: data.front_end_roas }) : null} highlight />
          </div>

          {/* Funnel operativo */}
          <div data-testid="ads-funnel" className="rounded-2xl border border-[color:var(--sp-border)] p-4">
            <div className="text-xs font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-3">{t("ads.funnel")}</div>
            <div className="flex flex-wrap items-center gap-2">
              <FunnelStep label={t("ads.leads")} value={data.leads} />
              <ArrowRight className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
              <FunnelStep label={t("ads.crmContacts")} value={data.unique_contacts ?? 0} />
              <ArrowRight className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
              <FunnelStep label={t("ads.funnelAppointments")} value={data.appointments_set} rate={data.setting_rate} />
              <ArrowRight className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
              <FunnelStep label={t("ads.showed")} value={data.showed_up} rate={data.show_up_rate} />
              <ArrowRight className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
              <FunnelStep label={t("ads.newCustomers")} value={data.new_customers} rate={data.lead_to_customer_rate} />
            </div>
            <p className="text-[11px] text-[color:var(--sp-text-muted)] mt-2">
              {t("ads.funnelNote")}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, badge, badgeOk, highlight, tid }) {
  return (
    <div data-testid={tid} className={`rounded-2xl border p-4 ${highlight ? "border-[color:var(--sp-brand)]/40 bg-[color:var(--sp-brand)]/5" : "border-[color:var(--sp-border)] bg-white"}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
      {sub && <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-0.5">{sub}</div>}
      {badge && (
        <div className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${badgeOk ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          <Facebook className="h-3 w-3" /> {badge}
        </div>
      )}
    </div>
  );
}

function FunnelStep({ label, value, rate }) {
  return (
    <div className="flex-1 min-w-[110px] rounded-xl bg-[color:var(--sp-subtle)] px-3 py-2 text-center">
      <div className="text-xl font-black">{fmtNum(value)}</div>
      <div className="text-[11px] font-bold">{label}</div>
      {rate != null && <div className="text-[10px] text-[color:var(--sp-text-muted)]">{rate.toFixed(1)}%</div>}
    </div>
  );
}
